import React, { useState, useEffect, useRef } from 'react';
import styles from './App.module.css';
import { getPlayerStats } from './api/brawlstars';
import StarEffect from './components/StarEffect';

interface PlayerStats {
  name: string;
  tag: string;
  trophies: number;
  highestTrophies: number;
  expLevel: number;
  expPoints: number;
  isQualifiedFromChampionshipChallenge: boolean;
  '3vs3Victories': number;
  soloVictories: number;
  duoVictories: number;
  bestRoboRumbleTime: number;
  bestTimeAsBigBrawler: number;
  icon: {
    id: number;
  };
  club: {
    tag: string;
    name: string;
  };
  brawlers: Array<{
    id: number;
    name: string;
    power: number;
    rank: number;
    trophies: number;
    highestTrophies: number;
  }>;
}

function App() {
  const [playerTag, setPlayerTag] = useState('#');
  const [playerStats, setPlayerStats] = useState<PlayerStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [volume, setVolume] = useState(0.5);
  const [showTopButton, setShowTopButton] = useState(false);
  const [isBGMPlaying, setIsBGMPlaying] = useState(false);
  const [wasManuallyTurnedOff, setWasManuallyTurnedOff] = useState(false);
  
  const buttonSound = useRef<HTMLAudioElement | null>(null);
  const bgm = useRef<HTMLAudioElement | null>(null);
  const volumeRef = useRef(volume);
  const [isAudioReady, setIsAudioReady] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);

  // BGM 상태 관리
  useEffect(() => {
    if (bgm.current) {
      if (isBGMPlaying && volume > 0) {
        bgm.current.play().catch(error => {
          console.error('BGM 재생 실패:', error);
        });
      } else {
        bgm.current.pause();
      }
    }

    return () => {
      if (bgm.current) {
        bgm.current.pause();
      }
    };
  }, [isBGMPlaying, volume]);

  // 갤럭시 AI 호출 감지
  useEffect(() => {
    const handleBlur = () => {
      if (!wasManuallyTurnedOff) {
        setIsBGMPlaying(false);
      }
    };

    const handleFocus = () => {
      if (!wasManuallyTurnedOff) {
        setIsBGMPlaying(true);
      }
    };

    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);

    return () => {
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [wasManuallyTurnedOff]);

  // 페이지 가시성 변경 감지
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // 페이지가 백그라운드로 전환되면 BGM 정지
        setIsBGMPlaying(false);
        if (bgm.current) {
          bgm.current.pause();
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // 사용자 상호작용 감지
  useEffect(() => {
    const handleUserInteraction = () => {
      setHasUserInteracted(true);
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };

    document.addEventListener('click', handleUserInteraction);
    document.addEventListener('keydown', handleUserInteraction);
    document.addEventListener('touchstart', handleUserInteraction);

    return () => {
      document.removeEventListener('click', handleUserInteraction);
      document.removeEventListener('keydown', handleUserInteraction);
      document.removeEventListener('touchstart', handleUserInteraction);
    };
  }, []);

  // 오디오 요소 초기화
  useEffect(() => {
    if (!hasUserInteracted) return;

    const buttonAudio = new Audio();
    const bgmAudio = new Audio();

    // 오디오 파일 형식 지원 확인
    const canPlayWav = buttonAudio.canPlayType('audio/wav');
    const canPlayMp3 = bgmAudio.canPlayType('audio/mpeg');

    console.log('WAV 지원:', canPlayWav);
    console.log('MP3 지원:', canPlayMp3);

    if (canPlayWav && canPlayMp3) {
      // 오디오 파일 경로 설정
      const buttonSoundPath = '/sounds/button.wav';
      const bgmPath = '/sounds/bgm.mp3';

      console.log('버튼 소리 경로:', buttonSoundPath);
      console.log('BGM 경로:', bgmPath);

      // 오디오 요소 생성 및 설정
      const createAudioElement = (src: string) => {
        const audio = new Audio();
        audio.src = src;
        audio.preload = 'auto';
        return audio;
      };

      buttonSound.current = createAudioElement(buttonSoundPath);
      bgm.current = createAudioElement(bgmPath);
      bgm.current.loop = true;
      bgm.current.volume = volumeRef.current;

      // 오디오 파일 로드 확인
      const checkAudioLoad = async () => {
        try {
          await Promise.all([
            new Promise((resolve, reject) => {
              if (buttonSound.current) {
                buttonSound.current.oncanplaythrough = resolve;
                buttonSound.current.onerror = reject;
              }
            }),
            new Promise((resolve, reject) => {
              if (bgm.current) {
                bgm.current.oncanplaythrough = resolve;
                bgm.current.onerror = reject;
              }
            })
          ]);
          console.log('오디오 파일 로드 성공');
          setIsAudioReady(true);
        } catch (error) {
          console.error('오디오 파일 로드 실패:', error);
        }
      };

      checkAudioLoad();
    } else {
      console.error('브라우저가 필요한 오디오 형식을 지원하지 않습니다');
    }

    // 컴포넌트 언마운트 시 정리
    return () => {
      if (buttonSound.current) {
        buttonSound.current.pause();
        buttonSound.current.src = '';  // 소스 제거
        buttonSound.current.load();    // 버퍼 해제
        buttonSound.current = null;
      }
      if (bgm.current) {
        bgm.current.pause();
        bgm.current.src = '';  // 소스 제거
        bgm.current.load();    // 버퍼 해제
        bgm.current = null;
      }
    };
  }, [hasUserInteracted]);

  // volume 값이 변경될 때마다 volumeRef와 오디오 볼륨 업데이트
  useEffect(() => {
    volumeRef.current = volume;
    if (bgm.current) {
      bgm.current.volume = volume;
    }
    if (buttonSound.current) {
      buttonSound.current.volume = volume;
    }
  }, [volume]);

  // BGM 재생/정지 관리
  useEffect(() => {
    const manageBGM = () => {
      if (volumeRef.current > 0 && isAudioReady && isBGMPlaying) {
        if (bgm.current) {
          if (bgm.current.paused) {
            bgm.current.currentTime = 0;
            bgm.current.play().catch(error => {
              console.error('BGM 재생 실패:', error);
            });
          }
        }
      } else if (bgm.current) {
        bgm.current.pause();
      }
    };

    manageBGM();

    const interval = setInterval(manageBGM, 1000);
    return () => clearInterval(interval);
  }, [isAudioReady, volume, isBGMPlaying]);

  // 로컬 스토리지에서 오디오 상태도 함께 불러오기
  useEffect(() => {
    const savedStats = localStorage.getItem('playerStats');
    const savedTag = localStorage.getItem('playerTag');
    const savedVolume = localStorage.getItem('volume');
    const savedBGMState = localStorage.getItem('isBGMPlaying');
    const savedManuallyOff = localStorage.getItem('wasManuallyTurnedOff');
    
    if (savedStats && savedTag) {
      try {
        setPlayerStats(JSON.parse(savedStats));
        setPlayerTag(savedTag);
      } catch (error) {
        console.error('로컬 스토리지 데이터 파싱 오류:', error);
      }
    }

    if (savedVolume) {
      const volumeValue = parseFloat(savedVolume);
      setVolume(volumeValue);
      volumeRef.current = volumeValue;
    }

    if (savedBGMState) {
      setIsBGMPlaying(savedBGMState === 'true');
    }

    if (savedManuallyOff) {
      setWasManuallyTurnedOff(savedManuallyOff === 'true');
    }

    setHasUserInteracted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const mainElement = document.querySelector(`.${styles.main}`);
      if (mainElement) {
        const scrollTop = mainElement.scrollTop;
        const scrollHeight = mainElement.scrollHeight;
        const clientHeight = mainElement.clientHeight;
        
        // 스크롤이 전체 높이의 20% 이상 내려갔을 때 버튼 표시
        if (scrollTop > (scrollHeight - clientHeight) * 0.2) {
          setShowTopButton(true);
        } else {
          setShowTopButton(false);
        }
      }
    };

    const mainElement = document.querySelector(`.${styles.main}`);
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll);
      return () => mainElement.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const scrollToTop = () => {
    const mainElement = document.querySelector(`.${styles.main}`);
    if (mainElement) {
      mainElement.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const playButtonSound = () => {
    if (buttonSound.current && isAudioReady) {
      // 이전 재생 중지
      buttonSound.current.pause();
      buttonSound.current.currentTime = 0;
      buttonSound.current.volume = 1.0; // 버튼 소리는 항상 최대 볼륨
      
      // 새로 재생
      buttonSound.current.play().catch(error => {
        console.error('버튼 소리 재생 실패:', error);
      });
    }
  };

  const handleSearch = async () => {

    if (playerTag.length < 6) {
      setError('최소 6글자 이상 입력해주세요');
      return;
    }
    
    playButtonSound();
    setLoading(true);
    setError('');

    try {
      const data = await getPlayerStats(playerTag);
      setPlayerStats(data);
      localStorage.setItem('playerStats', JSON.stringify(data));
      localStorage.setItem('playerTag', playerTag);
      
    } catch (err: any) {
      console.error('API Error:', err);
      try {
        const errorData = JSON.parse(err.message);
        if (errorData.details?.reason === 'inMaintenance') {
          setError('브롤스타즈 서버가 현재 점검 중입니다.');
        } else {
          setError('플레이어를 찾을 수 없습니다. 태그를 확인해주세요.');
        }
      } catch (parseError) {
        setError('플레이어를 찾을 수 없습니다. 태그를 확인해주세요.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setPlayerStats(null);
    setPlayerTag('#');
    setError('');
    localStorage.removeItem('playerStats');
    localStorage.removeItem('playerTag');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.toUpperCase();
    if (value.length === 0) {
      setPlayerTag('#');
    } else if (value[0] !== '#') {
      setPlayerTag('#' + value);
    } else {
      setPlayerTag(value);
    }
  };

  // 볼륨 변경 시 로컬 스토리지에 저장
  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    localStorage.setItem('volume', newVolume.toString());
    
    // 볼륨이 0보다 크면 BGM 켜기
    if (newVolume > 0 && !isBGMPlaying) {
      setIsBGMPlaying(true);
      setWasManuallyTurnedOff(false);
      localStorage.setItem('isBGMPlaying', 'true');
      localStorage.setItem('wasManuallyTurnedOff', 'false');
    }
  };

  const toggleBGM = () => {
    const newState = !isBGMPlaying;
    setIsBGMPlaying(newState);
    setWasManuallyTurnedOff(!newState);
    localStorage.setItem('isBGMPlaying', newState.toString());
    localStorage.setItem('wasManuallyTurnedOff', (!newState).toString());
  };

  const renderVolumeBars = () => {
    const bars = [];
    const totalBars = 10;
    const activeBars = isBGMPlaying ? Math.ceil(volume * totalBars) : 0;

    for (let i = 0; i < totalBars; i++) {
      const height = ((i + 1) / totalBars) * 100;
      bars.push(
        <div
          key={i}
          className={`${styles.volumeBar} ${i < activeBars ? styles.active : ''}`}
          style={{ height: `${height}%` }}
        />
      );
    }

    return bars;
  };

  return (
    <div className={`${styles.container} ${playerStats ? styles.scrollable : ''}`}>
      <div className={styles.bgPattern} />
      <header className={styles.header}>
        <div className={styles.headerLeft} onClick={handleReset} role="button" tabIndex={0}>
          <img src="./images/brawl-icon.png" alt="Brawl Stars Icon" />
          <span>BRAWL STARS</span>
        </div>
        <div className={styles.volumeControl}>
          <div 
            className={styles.volumeText}
            onClick={toggleBGM}
            role="button"
            tabIndex={0}
          >
            BGM
          </div>
          <div className={styles.volumeBars}>
            {renderVolumeBars()}
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className={styles.volumeSlider}
            />
          </div>
        </div>
        <div className={styles.homeButton} onClick={handleReset} role="button" tabIndex={0}>
          <img src="./images/home.png" alt="Home" />
        </div>
      </header>

      <main className={`${styles.main} ${playerStats ? styles.scrollable : ''}`}>
        {!playerStats && <StarEffect />}
        {!playerStats && (
          <div className={styles.eventLogo}>
            <img src="./images/event-logo.png" alt="Event Logo" />
          </div>
        )}
        
        <div className={`${styles.searchTitle} ${playerStats ? styles.searchTitleTop : ''}`}>
          <img src="./images/trophy-icon.png" alt="Trophy" className={styles.trophyIcon} />
          <span>브롤스타즈 전적 조회</span>
        </div>

        <div className={styles.searchContainer}>
          <div className={styles.searchBox}>
            <input
              type="text"
              value={playerTag}
              onChange={handleInputChange}
              placeholder="플레이어 태그를 입력하세요"
              className={styles.searchInput}
            />
            {!playerStats && (
              <span className={styles.searchInputHint}>태그 예시: #CPPC8282</span>
            )}
            <button
              onClick={handleSearch}
              disabled={loading || playerTag.length < 3}
              className={styles.searchButton}
            >
              {loading ? <div className={styles.loadingSpinner} /> : '조회'}
            </button>
          </div>
        </div>

        {error && <div className={styles.error}>{error}</div>}
        {!playerStats && (
          <>
            <div className={styles.infoText}>
              입력한 정보는 실시간으로만 조회되며,<br />
              따로 저장되거나 기록에 남지 않아요.
            </div>
            <div className={styles.titleScene}>
              <img src="./images/TitleScene.png" alt="Title Scene" />
            </div>
          </>
        )}

        {playerStats && (
          <div className={styles.playerStats}>
            <div className={styles.profileCard}>
              <div className={styles.playerInfo}>
                <h2>{playerStats.name}</h2>
              </div>
              <div className={styles.profileMainInfo}>
                <div className={styles.playerIcon}>
                  <img 
                    src={`https://cdn.brawlify.com/profile-icons/regular/${playerStats.icon.id}.png`} 
                    alt="Player Icon" 
                  />
                </div>
                <div className={styles.mainStats}>
                  <div className={styles.statRow}>
                    <img src="./images/level.png" alt="레벨" className={styles.statIcon} />
                    <span>레벨 <span>{playerStats.expLevel}</span></span>
                  </div>
                  <div className={styles.statRow}>
                    <img src="./images/trophy.png" alt="트로피" className={styles.statIcon} />
                    <span>트로피 <span>{playerStats.trophies}</span></span>
                  </div>
                </div>
              </div>
              <div className={styles.additionalStats}>
                <div className={styles.statRow}>
                  <img src="./images/highest-trophy.png" alt="최고트로피" className={styles.statIcon} />
                  <span>최고 트로피 <span>{playerStats.highestTrophies}</span></span>
                </div>
                <div className={styles.statRow}>
                  <img src="./images/3v3.png" alt="3vs3" className={styles.statIcon} />
                  <span>3vs3 승리 <span>{playerStats['3vs3Victories']}</span></span>
                </div>
                <div className={styles.statRow}>
                  <img src="./images/duo.png" alt="듀오" className={styles.statIcon} />
                  <span>듀오 승리 <span>{playerStats.duoVictories}</span></span>
                </div>
                <div className={styles.statRow}>
                  <img src="./images/solo.png" alt="솔로" className={styles.statIcon} />
                  <span>솔로 승리 <span>{playerStats.soloVictories}</span></span>
                </div>
              </div>
            </div>
            
            <div className={styles.brawlersSection}>
              <h3 className={styles.brawlerListTitle}>보유 브롤러 목록</h3>
              <h4 className={styles.topBrawlersTitle}>TOP 2</h4>
              <div className={styles.topBrawlers}>
                {playerStats.brawlers
                  .sort((a, b) => b.trophies - a.trophies)
                  .slice(0, 2)
                  .map(brawler => (
                    <div key={brawler.id} className={styles.topBrawlerCard}>
                      <div className={styles.brawlerIcon}>
                        <img src={`https://cdn.brawlify.com/brawlers/emoji/${brawler.id}.png`} alt={brawler.name} />
                      </div>
                      <h4>{brawler.name}</h4>
                      <div className={styles.brawlerStats}>
                        <p>
                          <img src="./images/trophy.png" alt="트로피" className={styles.statIcon} />
                          트로피 <span>{brawler.trophies}</span>
                        </p>
                        <p>
                          <img src="./images/highest-trophy.png" alt="최고트로피" className={styles.statIcon} />
                          최고 트로피 <span>{brawler.highestTrophies}</span>
                        </p>
                        <p>
                          <img src="./images/power.png" alt="파워레벨" className={styles.statIcon} />
                          파워레벨 <span>{brawler.power}</span>
                        </p>
                        <p>
                          <img src="./images/level.png" alt="랭크" className={styles.statIcon} />
                          랭크 <span>{brawler.rank}</span>
                        </p>
                      </div>
                    </div>
                  ))}
              </div>

              <h4 className={styles.otherBrawlersTitle}>그 외</h4>
              <div className={styles.otherBrawlers}>
                {playerStats.brawlers
                  .sort((a, b) => b.trophies - a.trophies)
                  .slice(2)
                  .map(brawler => (
                    <div key={brawler.id} className={styles.topBrawlerCard}>
                      <div className={styles.brawlerIcon}>
                        <img src={`https://cdn.brawlify.com/brawlers/emoji/${brawler.id}.png`} alt={brawler.name} />
                      </div>
                      <h4>{brawler.name}</h4>
                      <div className={styles.brawlerStats}>
                        <p>
                          <img src="./images/trophy.png" alt="트로피" className={styles.statIcon} />
                          트로피 <span>{brawler.trophies}</span>
                        </p>
                        <p>
                          <img src="./images/highest-trophy.png" alt="최고트로피" className={styles.statIcon} />
                          최고 트로피 <span>{brawler.highestTrophies}</span>
                        </p>
                        <p>
                          <img src="./images/power.png" alt="파워레벨" className={styles.statIcon} />
                          파워레벨 <span>{brawler.power}</span>
                        </p>
                        <p>
                          <img src="./images/level.png" alt="랭크" className={styles.statIcon} />
                          랭크 <span>{brawler.rank}</span>
                        </p>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        )}
      </main>

      {playerStats && (
        <button 
          className={`${styles.topButton} ${showTopButton ? styles.visible : ''}`}
          onClick={scrollToTop}
          aria-label="맨 위로 이동"
        />
      )}
    </div>
  );
}

export default App;
